import React, { useEffect } from 'react';

import {
    trackScreenExit,
    trackWidgetRendered,
    uniqueIdTransfer,
} from '@/apiCalls/uniqueIDTransferHelper';
import { EDisplays, selectDisplay, selectModal } from '@/context/display';
import { useAppSelector } from '@/context/storeHooks';

import { EEventType } from '../eventType';

export interface IDisplayComponentProps {
    baseId: string;
    children: React.ReactNode;
    className?: string;
    fillHeight?: boolean;
    [key: string]: unknown;
}

export const testId = 'display-component';

export function DisplayComponent({
    baseId,
    children,
    className,
    fillHeight = false,
    ...rest
}: IDisplayComponentProps): JSX.Element {
    const numChildren = React.Children.count(children);
    const displayScreen = useAppSelector(selectDisplay);
    const openModal = useAppSelector(selectModal);
    const changeScreenHeight =
        displayScreen === EDisplays.PREPPROVAL_ANIMATION ||
        displayScreen === EDisplays.OCR_ANIMATION;
    useEffect(() => {
        // The first screen to mount is the tool becoming visible. 'new' is the
        // weakest entry mode, so a return-user modal or a login that follows
        // will refine it rather than being overridden by it.
        trackWidgetRendered('new');

        uniqueIdTransfer({
            eventType: EEventType.VISIBLE_ON_SCREEN,
            applicationData: {
                id: `${baseId}-screen`,
            },
        });

        // Time on screen has to be measured in the browser: the gap between two
        // server receipt timestamps would include network latency and the
        // batching interval.
        const enteredAt =
            typeof performance !== 'undefined' ? performance.now() : Date.now();

        return () => {
            const exitedAt =
                typeof performance !== 'undefined'
                    ? performance.now()
                    : Date.now();
            trackScreenExit(`${baseId}-screen`, exitedAt - enteredAt);
        };
    }, []);
    return (
        <div
            id={`${baseId}-screen`}
            // The screen carries its own scrolling, so hiding overflow on the
            // wrapper above it was not enough to hold the page still behind a
            // modal.
            className={`_flex flex-col h-full ${
                changeScreenHeight ? 'min-h-[601px]' : 'max-h-full'
            } w-full ${
                openModal ? 'overflow-hidden' : 'overflow-y-auto'
            } ${className}`}
            // data-testid BEFORE the spread, so a caller can override it.
            //
            // These were the other way round, which meant the hardcoded default always
            // won and every `data-testid` a screen passed in was silently dropped —
            // React keeps the LAST occurrence of a duplicated prop. Loan, QuickCalculator,
            // IndicativelyApproved and DescriptionBox all pass one, all rendered as
            // 'display-component', and all four suites failed looking for their own id.
            // Nothing announced the loss: the attribute simply was not there.
            data-testid={testId}
            {...rest}
        >
            {React.Children.map(children, (child, index) => {
                if (index === numChildren - 1) {
                    return (
                        <div
                            className={`flex-1 px-4 ${
                                fillHeight
                                    ? 'flex flex-col min-h-0'
                                    : 'mt-auto _flex items-end'
                            }`}
                        >
                            {child}
                        </div>
                    );
                } else {
                    return <div className='px-4'>{child}</div>;
                }
            })}
        </div>
    );
}
